import { Activity, AlertCircle, ArrowDownRight, ArrowUpRight, Clock, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppCard from "../../components/AppCard";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { BarChart, LineChart } from "../../components/ui/Charts";
import { useDemo } from "../../providers/DemoProvider";
import { formatCurrency, formatDateTime, getKpis, getRevenueTrend } from "../../services/demoSelectors";
import { downloadCsv } from "../../utils/downloadCsv";

export default () => {
  const { state, setPendingPrompt } = useDemo();
  const navigate = useNavigate();
  const kpi = getKpis(state);
  const overdueRows = state.invoices.filter(item => item.status === "overdue").slice(0, 6);
  const riskProjects = state.projects.filter(project => project.burnRisk !== "low").slice(0, 6);

  const kpiCards = [
    { label: "Revenue MTD", value: formatCurrency(kpi.revenueMtd), delta: "+3.1%", trend: "up", icon: ArrowUpRight },
    { label: "Gross Profit", value: formatCurrency(kpi.grossProfit), delta: "-1.4%", trend: "down", icon: ArrowDownRight },
    { label: "Gross Margin", value: `${kpi.margin.toFixed(1)}%`, delta: `${kpi.marginDelta.toFixed(1)}pp`, trend: kpi.marginDelta >= 0 ? "up" : "down", icon: Activity },
    { label: "Cash In (7d)", value: formatCurrency(kpi.cashIn7d), delta: "-9.2%", trend: "down", icon: ArrowDownRight },
    { label: "Overdue", value: `${kpi.overdueCount}`, delta: formatCurrency(kpi.overdueAmount), trend: "neutral", icon: AlertCircle },
    { label: "Risk Jobs", value: `${kpi.projectsAtRisk}`, delta: "Review Needed", trend: "neutral", icon: Activity },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {kpiCards.map(card => (
          <button
            key={card.label}
            className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-3 text-left transition-all hover:border-indigo-500/50 hover:shadow-sm md:p-5"
            onClick={() => { setPendingPrompt(`Analyze ${card.label.toLowerCase()} and give me action steps.`); navigate("/dashboard/chat"); }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 transition-colors group-hover:text-indigo-500">{card.label}</span>
              <card.icon className={`h-3 w-3 md:h-4 md:w-4 ${card.trend === "up" ? "text-indigo-500" : card.trend === "down" ? "text-rose-500" : "text-slate-400"}`} />
            </div>
            <div className="mt-2 md:mt-4">
              <p className="text-lg font-bold text-slate-900 md:text-2xl">{card.value}</p>
              <div className="mt-1 flex items-center gap-1.5">
                <span className={`text-xs font-bold ${card.trend === "up" ? "text-indigo-600" : card.trend === "down" ? "text-rose-600" : "text-slate-500"}`}>{card.delta}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <AppCard title="Monthly Profit Trend" subtitle="Consolidated revenue performance" action={<Button variant="ghost" size="sm" className="h-8" onClick={() => { setPendingPrompt("Analyze monthly profit trend and forecast risk."); navigate("/dashboard/chat"); }}>Analyze with AI</Button>}>
          <LineChart data={getRevenueTrend(state)} />
        </AppCard>

        <AppCard title="Margin Leakage Categories" subtitle="Top cost-to-complete variances" action={<Button variant="ghost" size="sm" className="h-8" onClick={() => { setPendingPrompt("Where are we losing margin this month?"); navigate("/dashboard/chat"); }}>View Details</Button>}>
          <BarChart data={[{ label: "Materials Spikes", value: 98000 }, { label: "Overtime Uplift", value: 74000 }, { label: "Subcontract Rework", value: 53000 }, { label: "Travel & Logist.", value: 21000 }]} />
        </AppCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <AppCard
          title="Invoice Risk Queue"
          subtitle="Top aging receivables"
          action={<Button variant="outline" size="sm" className="h-8" onClick={() => downloadCsv("invoice-risk-queue.csv", [["Customer", "Amount", "Days Overdue"], ...overdueRows.map(item => [item.customer, item.amount.toString(), item.daysOverdue.toString()])])}><Download className="mr-2 h-3 w-3" /> Export CSV</Button>}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs font-semibold text-slate-400">
                <tr><th className="pb-3 pr-4">Customer</th><th className="pb-3 pr-4">Balance</th><th className="pb-3 pr-4">Days Overdue</th><th className="pb-3 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {overdueRows.map(item => (
                  <tr key={item.id} className="group transition-colors hover:bg-slate-50">
                    <td className="py-3 pr-4 font-medium text-slate-700">{item.customer}</td>
                    <td className="py-3 pr-4 font-mono font-semibold text-slate-500">{formatCurrency(item.amount)}</td>
                    <td className="py-3 pr-4"><Badge tone={item.daysOverdue > 10 ? "danger" : "warning"} label={`${item.daysOverdue} days`} size="sm" /></td>
                    <td className="py-3 text-right"><Button variant="ghost" size="sm" className="h-7 px-2">Remind</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AppCard>

        <AppCard title="Procore Burn-Rate Risk" subtitle="Live project financial health">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs font-semibold text-slate-400">
                <tr><th className="pb-3 pr-4">Project Name</th><th className="pb-3 pr-4">Variance</th><th className="pb-3 pr-4">Burn Status</th><th className="pb-3 text-right">Sync</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {riskProjects.map(item => (
                  <tr key={item.id} className="group transition-colors hover:bg-slate-50">
                    <td className="py-3 pr-4 font-medium text-slate-700">{item.name}</td>
                    <td className={`py-3 pr-4 font-mono font-semibold ${item.actualCost > item.budget ? "text-rose-600" : "text-emerald-600"}`}>
                      {item.actualCost > item.budget ? "+" : ""}{(((item.actualCost - item.budget) / item.budget) * 100).toFixed(1)}%
                    </td>
                    <td className="py-3 pr-4"><Badge tone={item.burnRisk === "high" ? "danger" : item.burnRisk === "medium" ? "warning" : "success"} label={item.burnRisk.toUpperCase()} size="sm" /></td>
                    <td className="py-3 text-right"><Clock className="ml-auto h-4 w-4 text-slate-300" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AppCard>
      </div>
    </div>
  );
};
